import React, { useState, useMemo } from "react";
import PropTypes from "prop-types";
import {
  Popper,
  Paper,
  TextField,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  Checkbox,
  Box,
  Typography,
  Button,
  Grow,
  makeStyles,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import { useTranslation } from "react-i18next";

const useStyles = makeStyles((theme) => ({
  popper: {
    zIndex: 1301, // Higher than dimension menu
  },
  paper: {
    width: 320,
    maxHeight: 500,
    display: "flex",
    flexDirection: "column",
    boxShadow: "0px 5px 15px 0px rgba(0, 0, 0, 0.3)",
  },
  header: {
    padding: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  headerTitle: {
    fontSize: "0.95rem",
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  headerSubtitle: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
  },
  searchContainer: {
    padding: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  searchField: {
    "& .MuiInput-underline": {
      "&:before": {
        borderBottomColor: theme.palette.divider,
      },
      "&:hover:not(.Mui-disabled):before": {
        borderBottomColor: theme.palette.text.primary,
      },
      "&:after": {
        borderBottomColor: (props) => `${props.clientColor} !important`,
      },
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: (props) => `${props.clientColor} !important`,
    },
    "& .MuiInputAdornment-root": {
      "& .MuiSvgIcon-root": {
        color: (props) => props.clientColor,
      },
    },
    "& input": {
      "&::placeholder": {
        color: theme.palette.text.secondary,
        opacity: 1,
      },
    },
  },
  checkbox: {
    color: (props) => `${props.clientColor} !important`,
    "&.Mui-checked": {
      color: (props) => `${props.clientColor} !important`,
    },
  },
  bulkActionsContainer: {
    display: "flex",
    gap: theme.spacing(1),
    padding: theme.spacing(1, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.grey[50],
  },
  bulkButton: {
    fontSize: "0.7rem",
    padding: theme.spacing(0.5, 1),
    textTransform: "none",
  },
  listContainer: {
    flex: 1,
    overflow: "auto",
    maxHeight: 300,
  },
  listItem: {
    padding: theme.spacing(0.5, 2),
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  listItemText: {
    "& .MuiListItemText-primary": {
      fontSize: "0.875rem",
    },
  },
  footer: {
    padding: theme.spacing(1.5, 2),
    borderTop: `1px solid ${theme.palette.divider}`,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    backgroundColor: theme.palette.grey[50],
  },
  counter: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
  },
  applyButton: {
    textTransform: "none",
    fontSize: "0.875rem",
  },
}));

export default function FilterValueSelector({
  open,
  anchorEl,
  selectedDimension,
  options,
  selected,
  onApply,
  onClose,
  clientColor,
}) {
  const classes = useStyles({ clientColor });
  const { t } = useTranslation("translation", {
    keyPrefix: "rgm.cockpit.filters.value_selector",
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [tempSelected, setTempSelected] = useState(selected);

  // Reset temp selection when opening or when selected changes
  React.useEffect(() => {
    setTempSelected(selected);
    setSearchQuery("");
  }, [selected, open]);

  // Filter options based on search
  const filteredOptions = useMemo(() => {
    if (!searchQuery) return options;
    return options.filter((opt) =>
      opt.toLowerCase().includes(searchQuery.toLowerCase()),
    );
  }, [options, searchQuery]);

  const handleToggle = (value) => {
    const currentIndex = tempSelected.indexOf(value);
    const newSelected = [...tempSelected];

    if (currentIndex === -1) {
      newSelected.push(value);
    } else {
      newSelected.splice(currentIndex, 1);
    }

    setTempSelected(newSelected);
  };

  const handleSelectAll = () => {
    setTempSelected([...options]);
  };

  const handleSelectNone = () => {
    setTempSelected([]);
  };

  const handleInvert = () => {
    const inverted = options.filter((opt) => !tempSelected.includes(opt));
    setTempSelected(inverted);
  };

  const handleApply = () => {
    onApply(tempSelected);
    onClose();
  };

  const handleCancel = () => {
    setTempSelected(selected);
    onClose();
  };

  if (!selectedDimension) return null;

  return (
    <Popper
      open={open}
      anchorEl={anchorEl}
      placement="right-start"
      transition
      className={classes.popper}
      modifiers={{
        flip: {
          enabled: false,
        },
        preventOverflow: {
          enabled: true,
          boundariesElement: "viewport",
        },
        offset: {
          enabled: true,
          offset: "0, 0",
        },
      }}
    >
      {({ TransitionProps }) => (
        <Grow {...TransitionProps}>
          <Paper className={classes.paper} elevation={2}>
            {/* Header */}
            <Box className={classes.header}>
              <Typography className={classes.headerTitle}>
                {selectedDimension.label}
              </Typography>
              <Typography className={classes.headerSubtitle}>
                {t("select_values")}
              </Typography>
            </Box>

            {/* Search */}
            <Box className={classes.searchContainer}>
              <TextField
                size="small"
                placeholder={t("search_placeholder", {
                  label: selectedDimension.label,
                })}
                fullWidth
                autoFocus
                value={searchQuery}
                className={classes.searchField}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </Box>

            {/* Bulk Actions */}
            <Box className={classes.bulkActionsContainer}>
              <Button
                size="small"
                onClick={handleSelectAll}
                className={classes.bulkButton}
              >
                {t("all")}
              </Button>
              <Button
                size="small"
                onClick={handleSelectNone}
                className={classes.bulkButton}
              >
                {t("none")}
              </Button>
              <Button
                size="small"
                onClick={handleInvert}
                className={classes.bulkButton}
              >
                {t("invert")}
              </Button>
            </Box>

            {/* Options List */}
            <Box className={classes.listContainer}>
              {filteredOptions.length > 0 ? (
                <List dense>
                  {filteredOptions.map((option) => (
                    <ListItem
                      key={option}
                      button
                      onClick={() => handleToggle(option)}
                      className={classes.listItem}
                    >
                      <Checkbox
                        edge="start"
                        checked={tempSelected.includes(option)}
                        tabIndex={-1}
                        disableRipple
                        size="small"
                        className={classes.checkbox}
                        style={{
                          color: tempSelected.includes(option)
                            ? clientColor
                            : undefined,
                        }}
                      />
                      <ListItemText
                        primary={option || t("blank")}
                        className={classes.listItemText}
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Box p={3} textAlign="center">
                  <Typography variant="body2" color="textSecondary">
                    {t("no_results")}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Footer */}
            <Box className={classes.footer}>
              <Typography className={classes.counter}>
                {t(
                  tempSelected.length === 1
                    ? "items_selected"
                    : "items_selected_plural",
                  { count: tempSelected.length },
                )}
              </Typography>
              <Box>
                <Button
                  size="small"
                  onClick={handleCancel}
                  className={classes.applyButton}
                >
                  {t("cancel")}
                </Button>
                <Button
                  size="small"
                  onClick={handleApply}
                  style={{ color: clientColor }}
                  className={classes.applyButton}
                >
                  {t("apply")}
                </Button>
              </Box>
            </Box>
          </Paper>
        </Grow>
      )}
    </Popper>
  );
}

FilterValueSelector.propTypes = {
  open: PropTypes.bool.isRequired,
  anchorEl: PropTypes.object,
  selectedDimension: PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
  }),
  options: PropTypes.array,
  selected: PropTypes.array,
  onApply: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  clientColor: PropTypes.string.isRequired,
};

FilterValueSelector.defaultProps = {
  options: [],
  selected: [],
};
